import React, { forwardRef, useMemo, ForwardRefRenderFunction, PropsWithChildren } from "react";
//#region types
import { CustomerFormTypes } from "models/formTypes";
import { FormHandles, BaseFormViewComponentProps } from "models/propTypes/genericFormProps";
import { SelectInputItemProps } from "models/propTypes/wintFormPropsTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { useWintForm, useWintWizard, WintForm, WizardFormSectionContainer } from "components/common/wintForms";
import CsvUploadStep from "./sections/import/csvUploadStep";
import AddressStep from "./sections/import/addressStep";
import InvoiceStep from "./sections/import/invoiceStep";
import ReferencePersonStep from "./sections/import/referenceStep";
import ImportCustomerFooter from "../import/importCustomersFooter";
import CustomerInfoStep from "./sections/import/customerInfoStep";
import useFortnoxAutoMapping from "../import/useFortnoxAutoMapping";
import RutRotStep from "./sections/import/rutRotStep";
//#endregion
import { CsvImportHelper, FileHelper, LocalizationHelperInstance } from "utils/helpers";

const CustomerImportForm: ForwardRefRenderFunction<FormHandles, BaseFormViewComponentProps> = (props, ref) => {
  const { wintFormProps, formState, connect, values, submitted, setValues, submit } =
    useWintForm<CustomerFormTypes.CustomerImport>({
      formRef: ref,
      defaultValues: props.defaultValues,
      validationSchema: props.validationSchema,
      handleSubmit: props.handleSubmit,
      onFormValidationChange: props.onFormValidationChange,
    });

  const wizard = useWintWizard(formState);

  const headers = useMemo((): SelectInputItemProps[] => {
    if (values.csvUpload?.length) {
      const fileContent = CsvImportHelper.decodeBase64(values.csvUpload[0].data);
      try {
        const { headers } = CsvImportHelper.importCSV(fileContent);
        return headers.map((item) => ({ value: FileHelper.getUTF8Encoding(item), label: item }));
      } catch (e: any) {
        formState.setFieldError(
          "csvUpload",
          e.message ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Invalid)
        );
      }
    }
    return [];
  }, [values.csvUpload]);

  useFortnoxAutoMapping(headers, values.csvUpload, setValues);

  return (
    <WintForm {...wintFormProps}>
      <WizardFormSectionContainer wizardProps={wizard}>
        <CsvUploadStep
          key="csvUpload"
          connect={connect}
          setValues={setValues}
          submitted={submitted}
          wizardProps={wizard}
          resetForm={formState.resetForm}
        />
        <ShowOnFileUpload values={values} headers={headers}>
          <CustomerInfoStep
            key="customer"
            connect={connect}
            setValues={setValues}
            submitted={submitted}
            wizardProps={wizard}
            headersObject={headers}
          />
        </ShowOnFileUpload>
        <ShowOnFileUpload values={values} headers={headers}>
          <AddressStep
            key="address"
            connect={connect}
            setValues={setValues}
            submitted={submitted}
            wizardProps={wizard}
            headersObject={headers}
          />
        </ShowOnFileUpload>
        <ShowOnFileUpload values={values} headers={headers}>
          <InvoiceStep
            key="invoice"
            connect={connect}
            setValues={setValues}
            submitted={submitted}
            wizardProps={wizard}
            headersObject={headers}
          />
        </ShowOnFileUpload>
        <ShowOnFileUpload values={values} headers={headers}>
          <ReferencePersonStep
            key="reference"
            connect={connect}
            setValues={setValues}
            values={values}
            submitted={submitted}
            wizardProps={wizard}
            headersObject={headers}
          />
        </ShowOnFileUpload>
        <ShowOnFileUpload values={values} headers={headers}>
          <RutRotStep
            key="reference"
            connect={connect}
            setValues={setValues}
            submitted={submitted}
            wizardProps={wizard}
            headersObject={headers}
          />
        </ShowOnFileUpload>
      </WizardFormSectionContainer>
      <ImportCustomerFooter submitForm={submit} wizardProps={wizard} onFormSubmitSuccess={props.onFormSubmitSuccess} />
    </WintForm>
  );
};

const ShowOnFileUpload: React.FC<
  PropsWithChildren<{ values: CustomerFormTypes.CustomerImport; headers: SelectInputItemProps[] }>
> = ({ values, headers, children }) => (values.csvUpload?.length && headers?.length ? <>{children}</> : <></>);

export default forwardRef(CustomerImportForm);
